import React, { useEffect, useState } from 'react';
import type { TemperatureLimits } from './dashboardCalculations';

const COL = {
  blue: '#00273F',
  green: '#1F9A3A',
  yellow: '#FFCA57',
  grey: '#969696',
  border: '#E2E5EA',
  surface: '#FFFFFF',
  text: '#464646',
  warnBg: '#FFF6DE',
  warnTxt: '#B8860B',
};

interface LimitsFieldsProps {
  draft: TemperatureLimits;
  onChange: (l: TemperatureLimits) => void;
  onApply: () => void;
  onClear: () => void;
  savedMsg: boolean;
}

const LimitsFields: React.FC<LimitsFieldsProps & { compact?: boolean }> = ({
  draft, onChange, onApply, onClear, savedMsg, compact,
}) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: 8,
      padding: '14px 18px',
      marginTop: compact ? 0 : 10,
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
    }}
  >
    <div style={{ fontSize: 13, fontWeight: 600, color: COL.blue, marginBottom: 4 }}>Temperature operating limits</div>
    <div style={{ fontSize: 11, color: COL.grey, marginBottom: 12 }}>
      Define min/max for each parameter. Joints outside these limits are flagged as excursions in the session detail view and the detailed table.
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {([
        ['Water temp (°F)', 'waterMin', 'waterMax'],
        ['Base temp (°F)', 'baseMin', 'baseMax'],
        ['Hardener temp (°F)', 'hardenerMin', 'hardenerMax'],
      ] as const).map(([label, minKey, maxKey]) => (
        <div key={label}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: COL.grey,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              marginBottom: 6,
            }}
          >
            {label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              value={draft[minKey]}
              onChange={e => onChange({ ...draft, [minKey]: Number(e.target.value) })}
              style={{
                width: 60,
                padding: '5px 8px',
                border: `1px solid ${COL.border}`,
                borderRadius: 4,
                fontSize: 12,
                textAlign: 'center',
                color: COL.text,
              }}
            />
            <span style={{ fontSize: 11, color: COL.grey }}>to</span>
            <input
              type="number"
              value={draft[maxKey]}
              onChange={e => onChange({ ...draft, [maxKey]: Number(e.target.value) })}
              style={{
                width: 60,
                padding: '5px 8px',
                border: `1px solid ${COL.border}`,
                borderRadius: 4,
                fontSize: 12,
                textAlign: 'center',
                color: COL.text,
              }}
            />
          </div>
        </div>
      ))}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={onApply}
        style={{
          padding: '6px 16px',
          border: 'none',
          borderRadius: 8,
          background: COL.blue,
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Apply limits
      </button>
      <button
        type="button"
        onClick={onClear}
        style={{
          padding: '6px 16px',
          border: `1px solid ${COL.border}`,
          borderRadius: 8,
          background: COL.surface,
          color: COL.text,
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Clear limits
      </button>
      {savedMsg && <span style={{ fontSize: 11, color: COL.green, marginLeft: 4 }}>Limits applied ✓</span>}
    </div>
  </div>
);

export interface PumpHouseLimitsToolbarProps {
  limits: TemperatureLimits;
  limitsActive: boolean;
  onPersist: (payload: { limits: TemperatureLimits; active: boolean }) => void;
  /**
   * `card` — standalone bordered block (legacy).
   * `inline` — Set limits button on the same row as “Spray operations overview”; panel wraps full width below.
   */
  variant?: 'card' | 'inline';
}

/**
 * Summary-page control for pump-house temperature limits (moved out of session drilldown).
 */
const PumpHouseLimitsToolbar: React.FC<PumpHouseLimitsToolbarProps> = ({
  limits,
  limitsActive,
  onPersist,
  variant = 'card',
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TemperatureLimits>(() => ({ ...limits }));
  const [savedMsg, setSavedMsg] = useState(false);

  useEffect(() => {
    if (open) setDraft({ ...limits });
  }, [limits, open]);

  function apply() {
    onPersist({ limits: draft, active: true });
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  }

  function clear() {
    onPersist({ limits, active: false });
    setDraft({ ...limits });
    setOpen(false);
  }

  const toggleBtn = (
    <button
      type="button"
      onClick={() => setOpen(v => !v)}
      style={{
        fontSize: 12,
        padding: '6px 14px',
        cursor: 'pointer',
        fontWeight: 500,
        transition: 'all 0.15s',
        border: `1px solid ${COL.yellow}`,
        borderRadius: 8,
        background: limitsActive ? COL.yellow : COL.warnBg,
        color: limitsActive ? COL.blue : COL.warnTxt,
        flexShrink: 0,
        alignSelf: 'flex-start',
      }}
    >
      ⚙ {limitsActive ? 'Limits active' : 'Set limits'}
    </button>
  );

  if (variant === 'inline') {
    return (
      <>
        {toggleBtn}
        {open && (
          <div style={{ flexBasis: '100%', width: '100%', paddingTop: 4 }}>
            <LimitsFields
              compact
              draft={draft}
              onChange={setDraft}
              onApply={apply}
              onClear={clear}
              savedMsg={savedMsg}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-gray-800">Temperature limits</div>
          <p className="text-xs text-gray-500 mt-0.5">
            Applied to session charts, excursion counts, and detailed row highlighting when active.
          </p>
        </div>
        {toggleBtn}
      </div>

      {open && (
        <LimitsFields draft={draft} onChange={setDraft} onApply={apply} onClear={clear} savedMsg={savedMsg} />
      )}
    </div>
  );
};

export default PumpHouseLimitsToolbar;
